import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Download, FileSpreadsheet, DollarSign, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { generatePayrollCSV, downloadCSV } from '@/components/utils/pdfGeneration';

export default function PayrollReports() {
  const [employers, setEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState('');
  const [employees, setEmployees] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [products, setProducts] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedEmployer) {
      generateReport();
    }
  }, [selectedEmployer]);

  const loadData = async () => {
    try {
      const [employersData, productsData] = await Promise.all([
        api.entities.Employer.list(),
        api.entities.BenefitProduct.list()
      ]);
      setEmployers(employersData);
      setProducts(productsData);

      // Tenant-scoped users will only see one employer; preselect it.
      if (employersData?.length === 1) {
        setSelectedEmployer(employersData[0].id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedEmployer) return;

    try {
      const [employeesData, enrollmentsData] = await Promise.all([
        api.entities.Employee.filter({ employer_id: selectedEmployer, is_active: true }),
        api.entities.Enrollment.filter({ employer_id: selectedEmployer, status: 'active' })
      ]);

      setEmployees(employeesData);
      setEnrollments(enrollmentsData);

      // Generate report data
      const data = [];
      employeesData.forEach(employee => {
        const empEnrollments = enrollmentsData.filter(e => e.employee_id === employee.id);
        
        empEnrollments.forEach(enrollment => {
          const product = products.find(p => p.id === enrollment.product_id);
          const monthlyPremium = enrollment.monthly_premium || 0;
          
          const payPeriods = {
            weekly: 52,
            bi_weekly: 26,
            semi_monthly: 24,
            monthly: 12
          };
          const periods = payPeriods[employee.pay_frequency] || 26;
          const perPaycheck = (monthlyPremium * 12) / periods;
          
          data.push({
            employeeId: employee.id,
            employeeName: `${employee.first_name} ${employee.last_name}`,
            ssnLast4: employee.ssn_last_four || '',
            benefitName: product?.name || 'Unknown',
            monthlyPremium,
            perPaycheck,
            payFrequency: employee.pay_frequency || 'bi_weekly'
          });
        });
      });

      setReportData(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate report');
    }
  };

  const handleExportCSV = () => {
    if (reportData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const csvContent = generatePayrollCSV(employees, enrollments, products);
    const employer = employers.find(e => e.id === selectedEmployer);
    const filename = `payroll_deductions_${employer?.company_name?.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    
    downloadCSV(csvContent, filename);
    toast.success('Payroll report exported');
  };

  const totalMonthly = reportData.reduce((sum, row) => sum + row.monthlyPremium, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Payroll Deduction Reports</h1>
          <p className="text-slate-500 mt-1">
            Generate reports for payroll processing
          </p>
        </div>
        
        <Button
          onClick={handleExportCSV}
          disabled={reportData.length === 0}
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Employer</Label>
              <Select value={selectedEmployer} onValueChange={setSelectedEmployer}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer..." />
                </SelectTrigger>
                <SelectContent>
                  {employers.map(employer => (
                    <SelectItem key={employer.id} value={employer.id}>
                      {employer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {selectedEmployer && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Employees</p>
                  <p className="text-2xl font-bold text-slate-900">{employees.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Monthly Deductions</p>
                  <p className="text-2xl font-bold text-slate-900">${totalMonthly.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Report Date</p>
                  <p className="text-lg font-bold text-slate-900">{format(new Date(), 'MMM d, yyyy')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Data Table */}
      {selectedEmployer && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Deduction Details</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee Name</TableHead>
                    <TableHead>SSN Last 4</TableHead>
                    <TableHead>Benefit</TableHead>
                    <TableHead>Monthly</TableHead>
                    <TableHead>Per-Paycheck</TableHead>
                    <TableHead>Pay Frequency</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{row.employeeName}</TableCell>
                      <TableCell>{row.ssnLast4}</TableCell>
                      <TableCell>{row.benefitName}</TableCell>
                      <TableCell>${row.monthlyPremium.toFixed(2)}</TableCell>
                      <TableCell className="font-semibold">${row.perPaycheck.toFixed(2)}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {row.payFrequency.replace('_', ' ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {reportData.length === 0 && (
                <div className="text-center py-12">
                  <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No active enrollments to display</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}